/**
 * Persists chat unread counters so badges survive full page reloads (e.g. /home
 * refresh) until the next sync from /chat.
 *
 * Routed through authStorage because these counts belong to whoever is signed
 * in: a support tab writing straight to localStorage would put the member's
 * badges on the operator's own chats.
 */
import { readAuth, writeAuth, removeAuth } from "../services/authStorage";

export const CHAT_UNREAD_STORAGE_KEY = "adoc_chat_unread_v1";

const sanitizeCount = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return Math.max(0, parsed);
};

const sanitizeMap = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    const id = String(k ?? "").trim();
    if (!id) continue;
    out[id] = sanitizeCount(v);
  }
  return out;
};

/** Map of id -> true for the "just opened, keep it read" flags. */
const sanitizeFlagMap = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    const id = String(k ?? "").trim();
    if (!id || !v) continue;
    out[id] = true;
  }
  return out;
};

/** @returns {{ chatUnread: { byChannelId: object, bySupportTypeId: object, recentlyReadIds: object, recentlyReadSupportIds: object } } | undefined} */
export function readPreloadedChatUnread() {
  try {
    const raw = readAuth(CHAT_UNREAD_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return undefined;
    return {
      chatUnread: {
        byChannelId: sanitizeMap(parsed.byChannelId),
        bySupportTypeId: sanitizeMap(parsed.bySupportTypeId),
        // These must survive a reload too. The backend caches each user's
        // unread for ~5 min, so a refresh right after reading a chat refetches
        // the pre-read count; without these flags the slice would take the
        // higher (stale) server value and the badge would come back on a chat
        // the user just opened.
        recentlyReadIds: sanitizeFlagMap(parsed.recentlyReadIds),
        recentlyReadSupportIds: sanitizeFlagMap(parsed.recentlyReadSupportIds),
      },
    };
  } catch {
    try {
      removeAuth(CHAT_UNREAD_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    return undefined;
  }
}

export function clearPersistedChatUnread() {
  try {
    removeAuth(CHAT_UNREAD_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function writePersistedChatUnread(chatUnread) {
  try {
    writeAuth(CHAT_UNREAD_STORAGE_KEY, JSON.stringify(chatUnread));
  } catch {
    /* ignore quota / private mode */
  }
}
